import React from "react"
import { Icon } from "../icon"
import { Kbd, KbdGroup } from "../kbd"
import { Tooltip } from "../tooltip"
import { VisuallyHidden } from "../visually-hidden"
import { cn } from "../utils"
import { MODIFIER_KEY } from "../utils/platform"
import { useNavigationContext } from "./navigation-context"
import { NavigationTooltip } from "./navigation-tooltip"

export type NavigationCommandTriggerProps = Omit<
  React.ComponentPropsWithoutRef<"button">,
  "children"
> & {
  /** Placeholder-style label shown in the expanded trigger (and the tooltip / accessible name when collapsed). */
  placeholder?: string
  /**
   * Keys rendered as a shortcut hint on the right, each in its own box.
   * Defaults to the platform's modifier (`⌘` on macOS, `Ctrl` elsewhere) + `K`.
   */
  keys?: string[]
}

// Mirrors NavigationLink's box so the trigger collapses like a nav row: the
// icon sits in a fixed `auto` grid column (pinned as the sidebar width
// animates) and the label fills `1fr`. The border stays 1px and only fades to
// transparent when collapsed, so there's no width jump.
const triggerStyles = [
  "grid grid-cols-[auto_1fr] items-center gap-x-2",
  "min-w-10 w-full h-10 rounded-[10px] p-2",
  "text-left text-sm text-gray-700 select-none",
  "hover:bg-gray-900/[0.06]",
  "outline-1 outline-gray-300 -outline-offset-1 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors"
].join(" ")

/**
 * A search-field-styled button for opening a command palette, sized for the
 * sidebar. It shares `Navigation.Item`'s collapse behaviour: the search icon
 * stays pinned while the label and shortcut hint clip away as the sidebar
 * narrows, and a label tooltip appears on hover when collapsed. It owns no
 * palette state; pass `onClick` to open one, keeping the design system
 * decoupled from any specific command menu.
 *
 * Place it directly inside `Navigation` (it reads the collapsed state from
 * context and includes its own sidebar spacing).
 *
 * @example
 * <Navigation.CommandTrigger onClick={() => setOpen(true)} />
 */
export const NavigationCommandTrigger = React.forwardRef<
  HTMLButtonElement,
  NavigationCommandTriggerProps
>(function NavigationCommandTrigger(
  {
    placeholder = "Search Kit",
    keys = [MODIFIER_KEY, "K"],
    className,
    ...props
  },
  ref
) {
  const { collapsed } = useNavigationContext()

  const trigger = (
    <button
      ref={ref}
      type="button"
      data-slot="navigation-command-trigger"
      className={cn(triggerStyles, className)}
      {...props}
    >
      <Icon name="search" className="shrink-0 text-gray-700" />
      {collapsed ? (
        <VisuallyHidden>{placeholder}</VisuallyHidden>
      ) : (
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate leading-6">
            {placeholder}
          </span>
          <KbdGroup className="mr-[0.125rem]" aria-hidden>
            {keys.map(key => (
              <Kbd key={key}>{key}</Kbd>
            ))}
          </KbdGroup>
        </span>
      )}
    </button>
  )

  return (
    <div className="px-2 pb-4">
      {/* Tooltip mirrors Navigation.Item: enabled only when collapsed, since the
          expanded field already shows the label. */}
      <Tooltip.Provider delay={0}>
        <Tooltip disabled={!collapsed} decorative>
          <Tooltip.Trigger render={trigger} />
          <Tooltip.Content position="right" sideOffset={12} maxWidth={192}>
            <NavigationTooltip label={placeholder} />
          </Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
})

NavigationCommandTrigger.displayName = "Navigation.CommandTrigger"
